import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PRACTICE_AREAS_NAV } from "@/constants/firmInfo";
import { DynamicIcon } from "@/components/shared/IconMap";
import { MotionStagger, MotionStaggerItem } from "@/components/shared/MotionReveal";
import { Watermark } from "@/components/shared/Watermark";
import { useLanguage } from "@/i18n/LanguageContext";
import { PRACTICE_AREAS_ES } from "@/i18n/translations";

const BULLETS = {
  "family-immigration": ["Spousal & fiancé(e) visas", "Parent & sibling petitions", "Adjustment of status"],
  "employment-immigration": ["H-1B specialty occupation", "L-1 intracompany transfers", "PERM & EB green cards"],
  "business-immigration": ["Visa sponsorship strategy", "Compliance support", "Workforce planning"],
  "us-citizenship": ["N-400 preparation", "Interview & civics prep", "Complex case guidance"],
  "business-law": ["LLC & corporation formation", "Contract drafting", "Business dispute guidance"],
  "wills-trusts": ["Wills & living trusts", "Powers of attorney", "Guardianship planning"],
  "family-law": ["Divorce representation", "Custody & conservatorship", "Support modifications"],
};

export const PracticeAreasGrid = () => {
  const { t, isSpanish } = useLanguage();

  return (
    <section className="relative py-16 sm:py-20 lg:py-28 bg-[color:var(--sand)] overflow-hidden" data-testid="practice-areas-section">
      <Watermark word="Counsel" position="bottom-left" className="text-[color:var(--navy-950)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-500)]">{t("practiceGrid.eyebrow")}</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[color:var(--navy-950)]">
            {t("practiceGrid.heading1")} <span className="italic text-[color:var(--gold-500)]">{t("practiceGrid.headingEmphasis")}</span> {t("practiceGrid.headingEnd")}
          </h2>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            {t("practiceGrid.subtitle")}
          </p>
        </div>

        <MotionStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRACTICE_AREAS_NAV.map((area, idx) => {
            const override = isSpanish ? PRACTICE_AREAS_ES[area.slug] : null;
            const title = override?.title || area.title;
            const bullets = override?.bullets || BULLETS[area.slug] || [];
            return (
              <MotionStaggerItem key={area.slug} className={idx === 0 ? "lg:col-span-2" : ""}>
                <Link
                  to={`/practice-areas/${area.slug}`}
                  className="group relative flex flex-col h-full rounded-3xl border border-slate-200/70 bg-white p-7 shadow-[0_10px_30px_rgba(11,18,32,0.05)] hover:shadow-[0_18px_50px_rgba(11,18,32,0.12)] hover:-translate-y-1 transition-all duration-300"
                  data-testid="practice-area-card"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="h-14 w-14 rounded-2xl bg-[color:var(--navy-950)] grid place-items-center shadow-[0_10px_20px_rgba(11,18,32,0.25)] group-hover:bg-[color:var(--gold-500)] transition-colors duration-300">
                      <DynamicIcon name={area.icon} className="h-6 w-6 text-[color:var(--gold-400)] group-hover:text-[color:var(--navy-950)] transition-colors duration-300" />
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-[color:var(--gold-500)] group-hover:translate-x-1 transition-all duration-300" />
                  </div>
                  <h3 className="font-heading text-xl text-[color:var(--navy-950)] mb-2.5">{title}</h3>
                  <ul className="space-y-1.5 flex-1">
                    {bullets.map((b) => (
                      <li key={b} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="mt-1.5 h-1 w-1 rounded-full bg-[color:var(--gold-500)] shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <span className="inline-flex items-center gap-1.5 mt-5 text-xs font-semibold uppercase tracking-wide text-[color:var(--navy-950)]/60 group-hover:text-[color:var(--gold-500)] transition-colors">
                    {t("practiceGrid.learnMore")}
                  </span>
                </Link>
              </MotionStaggerItem>
            );
          })}
        </MotionStagger>
      </div>
    </section>
  );
};
